/**
 * Helper functions for getting file paths from Capacitor
 * Converts Capacitor file URIs to native file paths for upload
 */

import { Capacitor } from '@capacitor/core'
import { Filesystem, Directory } from '@capacitor/filesystem'

/**
 * Get native file path and MIME type from Capacitor file URI
 */
export async function getFileForUpload(fileUri: string, mimeType?: string): Promise<{
  filePath: string
  mimeType: string
}> {
  // If it's already a native path
  if (fileUri.startsWith('file://') || !fileUri.includes('://')) {
    return {
      filePath: fileUri.replace('file://', ''),
      mimeType: mimeType || detectMimeType(fileUri),
    }
  }

  // If it's a Capacitor URL (capacitor://)
  if (fileUri.includes('capacitor://')) {
    // Convert to native path
    const nativePath = Capacitor.convertFileSrc(fileUri)
    return {
      filePath: nativePath.replace('file://', ''),
      mimeType: mimeType || detectMimeType(fileUri),
    }
  }

  // If you need to copy file to a known location first
  // (Sometimes needed for iOS)
  const fileName = fileUri.split('/').pop() || 'file.mp4'
//   const extension = fileName.split('.').pop()?.toLowerCase() || 'mp4'
  
  try {
    // Try to read the file and copy it to cache directory
    const fileData = await Filesystem.readFile({
      path: fileUri,
    })

    const copiedFile = await Filesystem.writeFile({
      path: fileName,
      data: fileData.data as string,
      directory: Directory.Cache,
    })

    return {
      filePath: copiedFile.uri.replace('file://', ''),
      mimeType: mimeType || detectMimeType(fileName),
    }
  } catch (error) {
    console.warn('Failed to copy file, using original path:', error)
    // Fallback: try to use the path as-is
    return {
      filePath: fileUri.replace('file://', ''),
      mimeType: mimeType || detectMimeType(fileUri),
    }
  }
}

/**
 * Detect MIME type from file extension
 */
function detectMimeType(filePath: string): string {
  const extension = filePath.split('.').pop()?.toLowerCase() || ''

  const mimeTypes: Record<string, string> = {
    // Images
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    bmp: 'image/bmp',
    // Videos
    mp4: 'video/mp4',
    mov: 'video/quicktime',
    avi: 'video/x-msvideo',
    mkv: 'video/x-matroska',
    wmv: 'video/x-ms-wmv',
    flv: 'video/x-flv',
    '3gp': 'video/3gpp',
    webm: 'video/webm',
  }

  return mimeTypes[extension] || 'application/octet-stream'
}
